import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HOME } from "../constants/testIds";
import Button from "../components/loadout/Button";
import { productsForBestsellers } from "../lib/shopify/queries";
import { formatINR, placeholderGradient, firstLetter } from "../lib/product";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const HERO_ROTATE_MS = 6000;
const LAST_PICKED_KEY = "hero.last_picked_id";

async function fetchHeroDrops() {
  try {
    const r = await fetch(`${BACKEND}/api/hero-drop/active`);
    if (!r.ok) return [];
    const j = await r.json();
    return j?.active ? (j.drops || []) : [];
  } catch {
    return [];
  }
}

// [3] Hero — 100vh first load / 75vh on repeat visit (cookie).
// Cursor parallax: max 8px translate on mousemove.  Right column: 3 vertical
// carousel cards, ±4deg rotateY on hover.  No motion library.
// Session cache of the last-known active drops. The homepage ALWAYS has a
// live hero drop, so repeat visitors should skip the static-fallback flash
// entirely and mount the video on first paint (fresh fetch revalidates in
// the background via placeholderData).
const HERO_DROPS_CACHE_KEY = "hero.drops.cache.v1";
function readHeroDropsCache() {
  try {
    const raw = window.sessionStorage.getItem(HERO_DROPS_CACHE_KEY);
    if (!raw) return undefined;
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return undefined;
    const valid = arr.filter((d) => d && d.id && d.video_url);
    return valid.length ? valid : undefined;
  } catch {
    return undefined;
  }
}
function writeHeroDropsCache(drops) {
  try {
    window.sessionStorage.setItem(HERO_DROPS_CACHE_KEY, JSON.stringify(drops));
  } catch { /* private mode */ }
}

export default function Hero() {
  const [firstVisit, setFirstVisit] = useState(true);
  useEffect(() => {
    try {
      if (typeof document !== "undefined") {
        if (document.cookie.includes("loadout_visited=1")) setFirstVisit(false);
        else document.cookie = "loadout_visited=1; max-age=" + 60 * 60 * 24 * 30 + "; path=/";
      }
    } catch {}
  }, []);

  const containerRef = useRef(null);
  const [par, setPar] = useState({ x: 0, y: 0 });

  // Interaction refs for auto-advance gating (no re-render needed).
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const pressRef = useRef(false);
  const inViewRef = useRef(true);

  const onMouseMove = (e) => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return;
    const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
    const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
    setPar({ x: nx, y: ny });
  };
  const onMouseLeave = () => { setPar({ x: 0, y: 0 }); hoverRef.current = false; };

  const { data } = useQuery({
    queryKey: ["shopify", "bestsellers", { first: 3 }],
    queryFn: () => productsForBestsellers({ first: 3 }),
    staleTime: 5 * 60_000,
  });
  const cards = (data?.products || []).slice(0, 3);

  // Admin-managed hero drops (optional). 0 → static brand fallback (no video
  // tag, headline + CTAs render as before). 1 → that single video, no picker,
  // no flicker. 2+ → rotation (Prompt 6).
  const { data: heroDrops = [] } = useQuery({
    queryKey: ["hero-drop", "active"],
    queryFn: fetchHeroDrops,
    staleTime: 60_000,
    retry: 0,
    // Repeat visitors get last-known drops synchronously → the video mounts
    // on the very first paint instead of after the fetch round-trip. Fresh
    // data still arrives via the background refetch and re-renders if the
    // lineup changed.
    placeholderData: readHeroDropsCache(),
  });

  // Refresh the session cache whenever a fresh (non-placeholder) list lands.
  useEffect(() => {
    if (heroDrops.length) writeHeroDropsCache(heroDrops);
  }, [heroDrops]);

  // Rotation index. null = not picked yet (initial paint renders the static
  // hero; the picker runs in useEffect after hydration — CSR-only app, so no
  // SSR mismatch, but the pattern stays SSR-safe).
  const [activeIdx, setActiveIdx] = useState(null);

  // MOUNT PICKER — position state: "first-shown".
  // Random pick on each fresh page load when 2+ records are active:
  //   • N >= 3: the last-shown record (sessionStorage hero.last_picked_id) is
  //     excluded from the candidate pool so consecutive loads differ.
  //   • N == 2: repeats are allowed but weighted 75/25 toward the record that
  //     was NOT shown last. With only two candidates a fair coin flip repeats
  //     the same video half the time, which reads as "broken" — the bias makes
  //     alternation likely without making it mechanical.
  useEffect(() => {
    if (!heroDrops.length) { setActiveIdx(null); return; }
    if (heroDrops.length === 1) { setActiveIdx(0); return; }
    setActiveIdx((cur) => {
      if (cur != null && cur < heroDrops.length) return cur; // keep pick across refetches
      let last = "";
      try { last = window.sessionStorage.getItem(LAST_PICKED_KEY) || ""; } catch { /* SSR/private */ }
      let pick;
      if (heroDrops.length === 2 && last) {
        const li = heroDrops.findIndex((d) => d.id === last);
        if (li >= 0) pick = Math.random() < 0.75 ? (li === 0 ? 1 : 0) : li;
      }
      if (pick == null) {
        let pool = heroDrops.map((_, i) => i);
        if (heroDrops.length >= 3 && last) {
          const li = heroDrops.findIndex((d) => d.id === last);
          if (li >= 0) pool = pool.filter((i) => i !== li);
        }
        pick = pool[Math.floor(Math.random() * pool.length)];
      }
      return pick;
    });
  }, [heroDrops]);

  // Remember what each visitor was last shown (drives next load's picker).
  useEffect(() => {
    if (activeIdx == null) return;
    const d = heroDrops[activeIdx];
    if (!d) return;
    try { window.sessionStorage.setItem(LAST_PICKED_KEY, d.id); } catch { /* private mode */ }
  }, [activeIdx, heroDrops]);

  // CHEVRON — position state: "explicit step". Advances by ordinal (upload
  // order), wraps at both ends.
  const step = useCallback((dir) => {
    setActiveIdx((cur) => {
      if (cur == null || heroDrops.length < 2) return cur;
      return (cur + dir + heroDrops.length) % heroDrops.length;
    });
  }, [heroDrops.length]);

  // Viewport visibility gate for auto-advance.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => { inViewRef.current = entry.isIntersecting; },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // AUTO-ADVANCE — position state: "linear advance". Every 6s while the hero
  // is in view, the tab is visible, the user isn't hovering/focusing/pressing,
  // and the OS hasn't requested reduced motion. Any pause condition simply
  // skips a tick; the next eligible tick resumes automatically.
  useEffect(() => {
    if (heroDrops.length < 2) return undefined;
    if (typeof window === "undefined") return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const t = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (!inViewRef.current || hoverRef.current || focusRef.current || pressRef.current) return;
      step(1);
    }, HERO_ROTATE_MS);
    return () => clearInterval(t);
  }, [heroDrops.length, step]);

  // Loading strategy: only the ACTIVE video mounts a <video> src. The next
  // ordinal candidate is preloaded into memory just before it could play.
  const activeDrop = activeIdx != null ? heroDrops[activeIdx] : null;
  useEffect(() => {
    if (activeIdx == null || heroDrops.length < 2) return undefined;
    const next = heroDrops[(activeIdx + 1) % heroDrops.length];
    if (!next) return undefined;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "video";
    link.href = `${BACKEND}${next.video_url}`;
    link.type = next.content_type || "video/mp4";
    document.head.appendChild(link);
    return () => link.remove();
  }, [activeIdx, heroDrops]);

  const heroDrop = activeDrop;

  const heightCls = firstVisit ? "h-screen min-h-[720px]" : "h-[75vh] min-h-[560px]";
  const headlineTx = { transform: `translate3d(${par.x * 6}px, ${par.y * 4}px, 0)`, transition: "transform 200ms ease-out" };
  const carouselTx = { transform: `translate3d(${par.x * -8}px, ${par.y * -8}px, 0)`, transition: "transform 200ms ease-out" };

  return (
    <section
      data-testid={HOME.heroSection}
      ref={containerRef}
      onMouseMove={onMouseMove}
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={onMouseLeave}
      onFocusCapture={() => { focusRef.current = true; }}
      onBlurCapture={() => { focusRef.current = false; }}
      onPointerDownCapture={() => { pressRef.current = true; }}
      onPointerUpCapture={() => { pressRef.current = false; }}
      className={"relative w-full overflow-hidden grid-pattern-hero " + heightCls}
    >
      {heroDrop && (
        <HeroVideoBackdrop key={heroDrop.id} drop={heroDrop} data-testid="home-hero-video" />
      )}
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 220px 20px #000" }} />

      <div className="relative mx-auto max-w-[1440px] px-6 h-full grid grid-cols-1 lg:grid-cols-[65fr_35fr] items-center gap-10">
        <div className="relative z-10" style={headlineTx}>
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C] mb-6">
            OPERATOR-GRADE · INDIA
          </div>
          <h1 className="font-display text-white leading-[0.92]">
            <span
              data-testid={HOME.heroHeadlineLine1}
              className="block text-[48px] md:text-[64px] lg:text-[96px]"
            >
              LOADOUT.
            </span>
            <span
              data-testid={HOME.heroHeadlineLine2}
              className="block text-[48px] md:text-[64px] lg:text-[96px] text-[#FF9F1C]"
            >
              LOCK IN.
            </span>
          </h1>
          <p
            data-testid={HOME.heroSubhead}
            className="mt-8 font-sans text-[16px] md:text-[18px] font-medium text-[#8A8A8A] max-w-[540px] leading-relaxed"
          >
            Operator-grade keyboards, mice, and surfaces — built for the Indian
            esports operator.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button
              as="a"
              href="/collections/best-sellers"
              variant="primary"
              data-testid={HOME.heroCtaPrimary}
            >
              Shop Bestsellers
            </Button>
            <Button
              as="a"
              href="/build-my-loadout"
              variant="secondaryDark"
              data-testid={HOME.heroCtaSecondary}
            >
              Build My Loadout
            </Button>
          </div>
        </div>

        {/* PROMPT 3.2 DEFECT D — right rail: internal-scroll option A.
            Sticky column with y-snap so all 3 cards are reachable without
            scrolling the page itself. Below-lg the rail stacks below the
            hero copy (see wrapping grid). When an admin hero drop is active,
            the rail is replaced by a single product spotlight card tied to
            the video. */}
        <div
          style={carouselTx}
          data-testid={HOME.heroRail}
          className="hero-rail hidden lg:flex flex-col gap-4 relative z-10 self-start"
        >
          {heroDrop
            ? <HeroDropCard drop={heroDrop} />
            : (cards.length === 0
                ? [0, 1, 2].map((i) => <SkeletonCard key={i} />)
                : cards.map((p) => <HeroCarouselCard key={p.id} product={p} />))}
        </div>
      </div>

      {/* Mobile rail — stacks below hero copy per PROMPT 3.2 §D. */}
      <div className="relative z-10 lg:hidden mx-auto max-w-[1440px] px-6 pb-12">
        <div
          data-testid="home-hero-rail-mobile"
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          {heroDrop
            ? <HeroDropCard drop={heroDrop} />
            : (cards.length === 0
                ? [0, 1, 2].map((i) => <SkeletonCard key={i} />)
                : cards.map((p) => <HeroCarouselCard key={p.id} product={p} />))}
        </div>
      </div>

      {/* Rotation controls — explicit-step chevrons, wrap-around. Only shown
          when 2+ records are active. */}
      {heroDrops.length >= 2 && activeIdx != null && (
        <div
          data-testid="hero-rotation-controls"
          className="absolute bottom-6 right-6 z-20 flex items-center gap-2"
        >
          <button
            type="button"
            data-testid="hero-rotation-prev"
            aria-label="Previous hero video"
            onClick={() => step(-1)}
            className="inline-flex h-10 w-10 items-center justify-center border border-white/25 bg-black/40 text-white/80 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors backdrop-blur-sm"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span
            data-testid="hero-rotation-position"
            className="font-mono text-[10px] tracking-[0.2em] text-white/60 tabular-nums"
          >
            {activeIdx + 1} / {heroDrops.length}
          </span>
          <button
            type="button"
            data-testid="hero-rotation-next"
            aria-label="Next hero video"
            onClick={() => step(1)}
            className="inline-flex h-10 w-10 items-center justify-center border border-white/25 bg-black/40 text-white/80 hover:border-[#FF9F1C] hover:text-[#FF9F1C] transition-colors backdrop-blur-sm"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <style>{`
        .hero-rail {
          max-height: min(680px, calc(100vh - 160px));
          overflow-y: auto;
          scroll-snap-type: y mandatory;
          padding-right: 6px;
          scrollbar-width: thin;
          scrollbar-color: #FF9F1C33 transparent;
        }
        .hero-rail::-webkit-scrollbar { width: 4px; }
        .hero-rail::-webkit-scrollbar-thumb { background: #FF9F1C55; border-radius: 2px; }
        .hero-rail > * { scroll-snap-align: start; flex: 0 0 auto; }
      `}</style>
    </section>
  );
}

// ============================================================================
// End of Hero component. Below: HeroCarouselCard + SkeletonCard.
// ============================================================================

function HeroCarouselCard({ product }) {
  const [hovered, setHovered] = useState(false);
  const price = formatINR(product.priceRange?.minVariantPrice);
  const image = product.featuredImage || product.images?.[0];
  const style = {
    transformStyle: "preserve-3d",
    transform: hovered ? "rotateY(-4deg) scale(1.02)" : "rotateY(0deg) scale(1)",
    transition: "transform 320ms ease-out, border-color 200ms ease-out",
  };
  return (
    <a
      data-testid={HOME.heroCarouselItem}
      href={`/p/${product.handle}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={style}
      className="relative w-[220px] h-[320px] bg-[#141414] border border-[#1F1F1F] hover:border-[#FF9F1C] overflow-hidden ml-auto"
    >
      <div className="relative w-[220px] h-[220px] bg-black">
        {image?.url ? (
          <img
            src={image.url}
            alt={image.altText || product.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: placeholderGradient(product.handle) }}
          >
            <span className="font-display text-white/60 text-5xl">
              {firstLetter(product.title)}
            </span>
          </div>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-3 flex items-end justify-between gap-2">
        <span className="font-sans text-[13px] font-semibold text-white leading-tight line-clamp-2">
          {product.title}
        </span>
        <span className="font-mono text-[13px] font-semibold text-[#FF9F1C] whitespace-nowrap">
          {price || "—"}
        </span>
      </div>
    </a>
  );
}

function SkeletonCard() {
  return (
    <div className="w-[220px] h-[320px] bg-[#141414] border border-[#1F1F1F] ml-auto animate-pulse">
      <div className="w-[220px] h-[220px] bg-[#1a1a1a]" />
      <div className="p-3 space-y-2">
        <div className="h-3 w-3/4 bg-[#1a1a1a]" />
        <div className="h-3 w-1/3 bg-[#1a1a1a]" />
      </div>
    </div>
  );
}

// Full-section muted-looped video backdrop. Short clip, preload="metadata"
// so iOS Safari / Android Chrome only pull the first MOOV bytes to decide
// aspect, and begin streaming the rest only once the user is on the page.
// A dark fade-overlay keeps the LOADOUT. LOCK IN. headline readable even on
// bright footage, matching the all-black backdrop in the reference image.
function HeroVideoBackdrop({ drop }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Belt-and-braces autoplay nudge for iOS Safari.
    const tryPlay = () => el.play().catch(() => {});
    tryPlay();
    const onVisible = () => { if (document.visibilityState === "visible") tryPlay(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [drop.id]);
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="home-hero-video-wrap">
      <video
        ref={ref}
        src={`${BACKEND}${drop.video_url}`}
        autoPlay muted loop playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Readability veil — darker on the left so headline stays legible. */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(10,10,10,0.78) 0%, rgba(10,10,10,0.55) 45%, rgba(10,10,10,0.25) 100%)",
        }}
      />
    </div>
  );
}

// Spotlight card linked to the active video — swaps in for the usual
// 3-card right rail when a hero drop is live.
function HeroDropCard({ drop }) {
  const href = drop.product_url || (drop.product_handle ? `/p/${drop.product_handle}` : "/");
  return (
    <a
      data-testid="home-hero-drop-card"
      href={href}
      className="relative block w-full lg:w-[300px] lg:ml-auto bg-black border border-[#FF9F1C]/40 hover:border-[#FF9F1C] transition-colors overflow-hidden"
    >
      <div className="relative w-full aspect-video bg-black">
        <video
          src={`${BACKEND}${drop.video_url}`}
          autoPlay muted loop playsInline preload="metadata"
          className="w-full h-full object-cover opacity-85"
        />
        <span className="absolute top-2 left-2 font-mono text-[10px] tracking-[0.18em] uppercase bg-[#FF9F1C] text-[#0A0A0A] px-2 py-1">
          New Drop
        </span>
      </div>
      <div className="p-4 flex flex-col gap-3">
        <p
          data-testid="home-hero-drop-title"
          className="font-display text-[18px] leading-tight uppercase text-white"
        >
          {drop.product_title || "New Drop"}
        </p>
        <div className="flex items-center justify-between gap-3">
          {drop.product_price_display ? (
            <span className="font-mono text-[14px] font-semibold text-[#FF9F1C]">
              {/^\d+(\.\d+)?$/.test(String(drop.product_price_display).replace(/,/g, "").trim())
                ? `₹${Number(String(drop.product_price_display).replace(/,/g, "")).toLocaleString("en-IN")}`
                : drop.product_price_display}
            </span>
          ) : <span />}
          <span
            data-testid="home-hero-drop-cta"
            className="inline-block border border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2"
          >
            {drop.cta_label || "Shop Now"} →
          </span>
        </div>
      </div>
    </a>
  );
}
